/* Home Advisor Hero — vanilla web component.
   Adapts the existing homepage-hero.liquid input behavior:
   - Typewriter placeholder cycling
   - Attach popover (photo / URL)
   - Photo upload (downscale to 1200px, JPEG)
   - Submit redirects to target URL with text/photo/url payload
   - Suggested prompt buttons insert text without auto-submit
   - Mood tabs activate corresponding board card visually
*/

class HomeAdvisorHero extends HTMLElement {
  constructor() {
    super();
    this.refs = {};
    this.pendingImage = null;
    this.tw = { active: true, index: 0, charCount: 0, timer: null, timeout: null, prompts: [] };
  }

  connectedCallback() {
    this.collectRefs();
    if (!this.refs.textarea) return;

    this.loadConfig();
    this.bindEvents();
    this.startTypewriter();
  }

  disconnectedCallback() {
    this.stopTypewriter();
  }

  collectRefs() {
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.dataset.ref] = el;
    });
  }

  loadConfig() {
    const node = document.querySelector('[data-home-advisor-hero-config]');
    if (!node) return;
    try {
      const cfg = JSON.parse(node.textContent);
      const prompts = (cfg.typewriterPrompts || []).map((s) => String(s).trim()).filter(Boolean);
      if (prompts.length) this.tw.prompts = prompts;
    } catch (e) {
      // ignore
    }
  }

  bindEvents() {
    const r = this.refs;
    r.textarea.addEventListener('focus', () => this.deactivateTypewriter());
    r.textarea.addEventListener('click', () => this.deactivateTypewriter());
    r.textarea.addEventListener('input', () => {
      this.updatePlaceholder();
      this.updateSend();
    });
    r.textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.submit(); }
    });

    r.sendBtn?.addEventListener('click', () => this.submit());

    r.attachBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !r.popover.hasAttribute('hidden');
      if (isOpen) r.popover.setAttribute('hidden', ''); else r.popover.removeAttribute('hidden');
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.hah__attach-wrap')) {
        r.popover?.setAttribute('hidden', '');
      }
    });

    r.popPhoto?.addEventListener('click', () => {
      r.fileInput?.click();
      r.popover?.setAttribute('hidden', '');
    });

    r.popUrl?.addEventListener('click', () => {
      r.popover?.setAttribute('hidden', '');
      r.urlWrap?.removeAttribute('hidden');
      r.urlInput.value = '';
      r.urlInput.focus();
    });

    r.urlCancel?.addEventListener('click', () => {
      r.urlInput.value = '';
      r.urlWrap?.setAttribute('hidden', '');
      this.updateSend();
    });

    r.urlInput?.addEventListener('input', () => this.updateSend());

    r.fileInput?.addEventListener('change', (e) => this.handleFile(e));
    r.photoRemove?.addEventListener('click', () => this.clearPhoto());

    // Suggested prompt buttons (siblings, not inside this element)
    const heroSection = this.closest('.home-advisor-hero');
    const suggested = heroSection?.querySelectorAll('[data-prompt]');
    suggested?.forEach((btn) => {
      btn.addEventListener('click', () => {
        const promptText = btn.dataset.prompt || '';
        // Strip surrounding quotes if present
        const cleaned = promptText.replace(/^["']|["']$/g, '');
        r.textarea.value = cleaned;
        this.deactivateTypewriter();
        this.updatePlaceholder();
        this.updateSend();
        r.textarea.focus();
      });
    });

    // Mood tabs (siblings within hero, also not inside this element)
    const heroRoot = this.closest('.home-advisor-hero');
    if (heroRoot) {
      const tabs = heroRoot.querySelectorAll('.home-advisor-hero__mood-tab');
      const boards = heroRoot.querySelectorAll('[data-mood-board]');
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = tab.dataset.mood;
          tabs.forEach((t) => {
            t.classList.toggle('is-active', t === tab);
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
          });
          boards.forEach((b) => {
            const isActive = b.dataset.moodBoard === idx;
            b.classList.toggle('is-highlighted', isActive);
            const badge = b.querySelector('.home-advisor-hero__badge');
            const cta = b.querySelector('.home-advisor-hero__board-cta');
            if (cta) {
              cta.classList.toggle('home-btn--ink', isActive);
              cta.classList.toggle('home-btn--ghost', !isActive);
              cta.textContent = isActive ? 'Open this board' : 'View board';
            }
            if (isActive && !badge) {
              const newBadge = document.createElement('span');
              newBadge.className = 'home-advisor-hero__badge';
              newBadge.textContent = 'Most matched';
              b.prepend(newBadge);
            } else if (!isActive && badge) {
              badge.remove();
            }
          });
        });
      });
    }
  }

  hasContent() {
    const r = this.refs;
    return !!(r.textarea.value.trim() || this.pendingImage || (r.urlInput && r.urlInput.value.trim()));
  }

  updateSend() {
    const r = this.refs;
    if (!r.sendBtn) return;
    r.sendBtn.classList.toggle('is-active', this.hasContent());
  }

  updatePlaceholder() {
    const r = this.refs;
    if (this.tw.active) return;
    if (r.textarea.value) {
      r.placeholder?.setAttribute('hidden', '');
    } else {
      r.placeholder?.removeAttribute('hidden');
    }
  }

  startTypewriter() {
    const prompts = this.tw.prompts;
    if (!prompts.length) {
      this.refs.placeholder?.setAttribute('hidden', '');
      return;
    }
    this.tw.charCount = 0;
    const prompt = prompts[this.tw.index];
    this.tw.timer = setInterval(() => {
      if (this.tw.charCount < prompt.length) {
        this.tw.charCount++;
        this.refs.phText.textContent = prompt.slice(0, this.tw.charCount);
      } else {
        clearInterval(this.tw.timer);
        this.tw.timer = null;
        this.tw.timeout = setTimeout(() => {
          this.tw.index = (this.tw.index + 1) % prompts.length;
          this.startTypewriter();
        }, 2500);
      }
    }, 38);
  }

  stopTypewriter() {
    if (this.tw.timer) { clearInterval(this.tw.timer); this.tw.timer = null; }
    if (this.tw.timeout) { clearTimeout(this.tw.timeout); this.tw.timeout = null; }
  }

  deactivateTypewriter() {
    if (!this.tw.active) return;
    this.tw.active = false;
    this.stopTypewriter();
    this.refs.placeholder?.setAttribute('hidden', '');
  }

  handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const maxDim = 1200;
      const w = img.width;
      const h = img.height;
      const scale = (w > maxDim || h > maxDim) ? maxDim / Math.max(w, h) : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          this.pendingImage = reader.result;
          const r = this.refs;
          r.photoThumb.src = this.pendingImage;
          r.photoName.textContent = file.name || 'photo.jpg';
          r.photoRow?.removeAttribute('hidden');
          this.updateSend();
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.85);
    };
    img.src = objectUrl;
  }

  clearPhoto() {
    this.pendingImage = null;
    const r = this.refs;
    r.photoRow?.setAttribute('hidden', '');
    if (r.photoThumb) r.photoThumb.src = '';
    this.updateSend();
  }

  submit() {
    if (!this.hasContent()) return;
    const target = this.dataset.targetUrl || '/a/advisor';
    const r = this.refs;
    const text = r.textarea.value.trim();
    const url = r.urlInput?.value.trim() || '';
    document.dispatchEvent(new CustomEvent('home:advisor-submit', {
      detail: { query: text, has_photo: !!this.pendingImage, has_url: !!url },
    }));

    if (this.pendingImage || url) {
      try {
        sessionStorage.setItem('launch:handoff', JSON.stringify({
          text: text || (this.pendingImage ? "Here's a photo" : ''),
          image: this.pendingImage || null,
          url: url || null,
        }));
      } catch (err) { /* ignore */ }
      window.location.href = target;
    } else {
      const sep = target.includes('?') ? '&' : '?';
      window.location.href = `${target}${sep}q=${encodeURIComponent(text)}`;
    }
  }
}

if (!customElements.get('home-advisor-hero')) {
  customElements.define('home-advisor-hero', HomeAdvisorHero);
}
